
import React from 'react';
import { format } from 'date-fns';
import { Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Procurement } from '@/types/database.types';
import { SortConfig } from '@/hooks/use-seller-procurements';
import { calculateTotalAmount } from '@/utils/procurementCalculations';

interface SellerProcurementTableProps {
  procurements: Procurement[];
  sortConfig: SortConfig;
  handleSort: (key: string) => void;
}

const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case 'completed':
      return 'default';
    case 'rejected':
      return 'destructive';
    case 'pending_finance':
    case 'awaiting_approval':
      return 'secondary';
    case 'delivered':
    case 'price_confirmed':
      return 'outline';
    default:
      return 'outline';
  }
};

const SellerProcurementTable: React.FC<SellerProcurementTableProps> = ({
  procurements,
  sortConfig,
  handleSort,
}) => {
  const getProductsSummary = (procurement: Procurement) => {
    if (procurement.items && procurement.items.length > 0) {
      if (procurement.items.length === 1) {
        return procurement.items[0].product_name;
      }
      return `${procurement.items.length} products`;
    }
    
    // Fallback to single product name if items not loaded
    return procurement.product_name || 'N/A';
  };

  const getTotalQuantity = (procurement: Procurement) => {
    if (procurement.items && procurement.items.length > 0) {
      return procurement.items.reduce((total, item) => total + item.quantity, 0);
    }
    return procurement.total_quantity || 0;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead onClick={() => handleSort('created_at')} className="cursor-pointer">
            Date {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </TableHead>
          <TableHead>ID</TableHead>
          <TableHead>Source</TableHead>
          <TableHead onClick={() => handleSort('product_name')} className="cursor-pointer">
            Products {sortConfig.key === 'product_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </TableHead>
          <TableHead className="text-right">Quantity</TableHead>
          <TableHead className="text-right cursor-pointer" onClick={() => handleSort('total_amount')}>
            Total Amount {sortConfig.key === 'total_amount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </TableHead>
          <TableHead onClick={() => handleSort('status')} className="cursor-pointer">
            Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {procurements.map((procurement) => (
          <TableRow key={procurement.id}>
            <TableCell>{format(new Date(procurement.created_at), 'MMM d, yyyy')}</TableCell>
            <TableCell className="font-mono text-xs">
              {procurement.id.substring(0, 8)}...
            </TableCell>
            <TableCell>
              <Badge variant="outline">{procurement.source_type}</Badge>
            </TableCell>
            <TableCell>{getProductsSummary(procurement)}</TableCell>
            <TableCell className="text-right">
              {getTotalQuantity(procurement).toFixed(2)} kg
            </TableCell>
            <TableCell className="text-right font-medium">
              Br {calculateTotalAmount(procurement).toFixed(2)}
            </TableCell>
            <TableCell>
              <Badge variant={getStatusBadgeVariant(procurement.status)}>
                {procurement.status.replace('_', ' ')}
              </Badge>
            </TableCell>
            <TableCell className="text-right">
              <Button
                variant="ghost"
                size="icon"
                asChild
                title="View details"
              >
                <Link to={`/procurements/${procurement.id}`}>
                  <Eye className="h-4 w-4" />
                </Link>
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default SellerProcurementTable;
